<script setup>
import {defineExpose, defineProps, nextTick, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {isPositiveNumber} from '@shared/utils/helpers'
import {authenticationStore} from '@/main'
import {accessRules} from '@/permissions/accessControl'
import {useIsRouteActive} from '@shared/composables/router/useIsRouteActive'
import {findFirstAvailableRoute, findRouteByName} from '@shared/utils/router'
import CountBadge from '@shared-components/ui/badges/CountBadge.vue'
import RestrictedAccessMark from '@/components/ui/badges/RestrictedAccessMark.vue'

const auth = authenticationStore
const router = useRouter()
const route = useRoute()

// Props & emits
const props = defineProps({
  id: {
    type: String,
    default: 'sidebar-dropdown',
  },
  parentRouteName: {
    type: String,
    default: '',
    required: true,
  },
  data: {
    type: Object,
    required: true,
  },
  isMini: {
    type: [Boolean, Number],
    default: false,
  },
  isCounter: {
    type: Boolean,
    default: false,
  },
})

// Refs
const showDropdownMenu = ref(false)
const visibleMenuLinks = ref([])

// Methods
const toggleDropdownAndRoute = async () => {
  if (!showDropdownMenu.value) {
    redirectToFirstAvailableRoute()
  }

  toggleDropdownMenu()
}

const redirectToFirstAvailableRoute = async () => {
  const target = findFirstAvailableRoute(accessRules, props.data?.core?.parentRoute, auth, router).name
  await router.push({name: target})
}

const toggleDropdownMenu = () => {
  showDropdownMenu.value = !showDropdownMenu.value
}

const closeDropdownMenu = () => {
  showDropdownMenu.value = false
}

watch(
  () => route.path,
  async () => {
    await nextTick()
    const navDropdown = document.getElementById(props.id)
    if (!navDropdown) {
      return
    }

    visibleMenuLinks.value = navDropdown.querySelectorAll('.nav-dropdown-menu-item')
    showDropdownMenu.value = Array.from(visibleMenuLinks.value).some(link => link.classList.contains('active'))
  },
  {immediate: true},
)

defineExpose({toggleDropdownMenu, closeDropdownMenu})
</script>

<template>
  <template v-if="isMini">
    <div
      v-if="data?.items?.length > 1"
      class="nav-item nav-item__mini"
      @mouseenter="showDropdownMenu = true"
      @mouseleave="showDropdownMenu = false"
    >
      <button
        @click="redirectToFirstAvailableRoute"
        class="nav-dropdown-button btn w-100 text-start sidebar-dropdown-router-link-button text-decoration-none"
        :class="{active: useIsRouteActive(props.data?.core?.parentRoute).value}"
        type="button"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-dropdown-button-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
              <span v-if="isCounter" class="nav-icon__notification-bullet" />
            </span>
          </span>
        </span>
      </button>

      <div v-show="showDropdownMenu" class="nav-item__menu">
        <ul class="nav-item__menu-ul">
          <template v-for="(item, index) in data?.items" :key="index">
            <li v-if="item.access">
              <router-link
                :to="item.params ? {name: item.routeName, params: item.params} : {name: item.routeName}"
                :class="['nav-dropdown-menu-item', useIsRouteActive(item.routeName, item.params).value ? 'active' : '']"
                active-class="active"
              >
                <span class="nav-text">{{ item.title }} <restricted-access-mark v-if="item.audience" :audience="item.audience" /></span>

                <count-badge
                  v-if="isPositiveNumber(item.counter)"
                  :count="item.counter"
                  background="#ff671f"
                  color="#fff"
                  class="custom mr-2"
                />
              </router-link>
            </li>
          </template>
        </ul>
      </div>
    </div>

    <div
      v-else-if="data?.items?.length === 1"
      class="nav-item nav-item__mini"
      v-tooltip.right="findRouteByName(data?.items[0].routeName, router)?.meta?.title"
    >
      <router-link
        :class="['nav-link', useIsRouteActive(data?.items[0].routeName, data?.items[0].params).value ? 'active' : '']"
        :to="
          data?.items[0].params
            ? {name: data?.items[0].routeName, params: data?.items[0].params}
            : {name: data?.items[0].routeName}
        "
        active-class="active"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-link-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
              <span v-if="isCounter" class="nav-icon__notification-bullet" />
            </span>
          </span>
        </span>
      </router-link>
    </div>
  </template>
  <template v-else>
    <div v-if="data?.items?.length > 1" class="nav-dropdown" :id="id">
      <button
        @click="toggleDropdownAndRoute"
        class="nav-dropdown-button btn w-100 text-start sidebar-dropdown-router-link-button text-decoration-none"
        type="button"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-dropdown-button-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
            </span>

            <span class="nav-text">{{ data?.mainButton?.title }}</span>
          </span>
          <count-badge
            v-if="isPositiveNumber(data?.mainButton?.counter)"
            :count="data?.mainButton?.counter"
            background="#ff671f"
            color="#fff"
            class="custom mr-4"
          />
        </span>

        <i
          :class="[
            'fa-light fa-chevron-up sidebar-dropdown-arrow custom fs-5',
            !showDropdownMenu ? 'fa-rotate-180' : '',
          ]"
        ></i>
      </button>

      <ul v-show="showDropdownMenu" class="nav-dropdown-menu">
        <template v-for="(item, index) in data?.items" :key="index">
          <li v-if="item.access">
            <router-link
              :to="item.params ? {name: item.routeName, params: item.params} : {name: item.routeName}"
              :class="['nav-dropdown-menu-item', useIsRouteActive(item.routeName, item.params).value ? 'active' : '']"
              active-class="active"
            >
              <span class="nav-text">{{ item.title }} <restricted-access-mark v-if="item.audience" :audience="item.audience" /></span>

              <count-badge
                v-if="isPositiveNumber(item.counter)"
                :count="item.counter"
                background="#ff671f"
                color="#fff"
                class="custom mr-2"
              />
            </router-link>
          </li>
        </template>
      </ul>
    </div>
    <div v-else-if="data?.items?.length === 1" class="nav-item">
      <router-link
        :class="['nav-link', useIsRouteActive(data?.items[0].routeName, data?.items[0].params).value ? 'active' : '']"
        :to="
          data?.items[0].params
            ? {name: data?.items[0].routeName, params: data?.items[0].params}
            : {name: data?.items[0].routeName}
        "
        active-class="active"
      >
        <span class="flex-1-1-auto d-flex align-items-center justify-content-between nav-link-inner">
          <span class="d-flex align-items-center">
            <span class="nav-icon">
              <slot name="button-icon" />
            </span>
            <span class="nav-text">{{ findRouteByName(data?.items[0].routeName, router)?.meta?.title }}</span>
          </span>
          <count-badge
            v-if="isPositiveNumber(data?.items[0].counter)"
            :count="data?.items[0].counter"
            background="#ff671f"
            color="#fff"
            class="custom mr-4"
          />
        </span>
      </router-link>
    </div>
  </template>
</template>

<style lang="scss">
.sidebar-dropdown-arrow {
  font-size: 20px;
  transition: all linear 0.1s;
  color: #6b7280;
}

.sidebar-dropdown-router-link-button {
  text-decoration: none;
  border: none !important;

  &:focus,
  &:active {
    border: none !important;
    box-shadow: none !important;
  }
}

.nav-icon {
  position: relative;
}

.nav-icon__notification-bullet {
  width: 10px;
  height: 10px;
  background: #ff671f;
  border-radius: 50%;
  position: absolute;
  border: 1px solid #fff;
  top: 0;
  right: -4px;
}

.nav-item__mini {
  position: relative;
  z-index: 93;

  & .nav-dropdown-button {
    border-right: 4px solid transparent !important;

    &.active {
      color: #030712;
      border-right: 4px solid #1f2937 !important;
    }
  }
}

.nav-item__menu {
  position: absolute;
  left: 100%;
  top: 0;
  z-index: 101;
  background: transparent;

  padding-left: 8px;

  & .nav-dropdown-menu-item {
    padding: 10px 12px;
    font-size: 16px;
  }
}

.nav-item__menu-ul {
  width: 240px;
  background: #fff;
  box-shadow: 0 2px 10px 4px rgba(0, 0, 0, 0.15);
  list-style: none;
  border-radius: 8px;
  margin: 0;
  padding: 8px;
}
</style>
